import React from 'react';
import redial from 'redial';
import {withLegacyProviders} from '../../LegacyComponentWrapper';

import {TrackingPageTypeWithId} from '@aboutyou/legacy-tracking-sdk';

import {useRouter} from '@aboutyou/legacy-desktop/src/app/helpers/navigation';
import {useTracking} from '@aboutyou/legacy-desktop/src/app/helpers/tracking/hooks/useTrackingContextAndClientV6';
import {useTrackingPageUrl} from '@aboutyou/legacy-desktop/src/app/helpers/tracking/hooks/useTrackingPageUrl';
import {PageTrackingContextProvider} from '@aboutyou/legacy-desktop/src/app/helpers/tracking/PageTrackingContext';
import TrackContentPageView from '@aboutyou/legacy-desktop/src/app/helpers/tracking/trackerComponents/TrackContentPageView';
import {useService} from '@aboutyou/legacy-desktop/src/app/services/utils/context';
import {
  useHelpCenterArticlePageStore,
  useUserStore,
} from '@aboutyou/legacy-desktop/src/app/store/context';
import {useLocale} from '@aboutyou/legacy-desktop/src/app/store/features/locale/useLocale';

import {useHasDroppedMobileSubdomain} from '@aboutyou/config';
import {useIsSoftFeatureToggleActive} from '@aboutyou/feature-flags';
import redialHandle from '@aboutyou/legacy-desktop/src/app/pages/HelpCenter/redialHelpCenterArticlePage';
import {
  useHelpCenterAlternateLink,
  useHelpCenterArticlePageCanonicalUrl,
} from '@aboutyou/legacy-desktop/src/app/pages/HelpCenter/seo';
import useHelpCenterTranslations from '@aboutyou/legacy-desktop/src/app/pages/HelpCenter/useHelpCenterTranslations';
import LegacyPage from '@aboutyou/legacy-shared/src/pages/HelpCenter/HelpCenterArticlePage';
import {CloudflareTurnstile} from '@aboutyou/logic-components';

const ConnectedHelpCenterArticlePage: React.FC<
  React.PropsWithChildren
> = () => {
  const {TrackingClient} = useTracking();
  const {navigateClient} = useRouter();

  const activeArticle = useHelpCenterArticlePageStore(
    store => store.activeArticle,
  );
  const translations = useHelpCenterTranslations({
    activeArticleName: activeArticle?.name,
  });
  const sections = useHelpCenterArticlePageStore(store => store.sections ?? []);
  const articleId = useHelpCenterArticlePageStore(store => store.articleId);
  const activeSectionId = useHelpCenterArticlePageStore(
    store => store.activeSectionId,
  );
  const relatedArticles = useHelpCenterArticlePageStore(
    store => store.relatedArticles ?? [],
  );
  const canonical = useHelpCenterArticlePageCanonicalUrl();
  const {path, search} = useTrackingPageUrl();
  const hasDroppedMobileSubdomain = useHasDroppedMobileSubdomain();
  const alternateLink = useHelpCenterAlternateLink(
    canonical,
    hasDroppedMobileSubdomain,
  );
  const {helpCenter} = useService();
  const {locale} = useLocale();
  const isVip = useUserStore(store => store.isVip);

  const isShowOldHelpCenterEnabled =
    useIsSoftFeatureToggleActive('show_old_help_center') === true;

  return (
    <PageTrackingContextProvider
      id={articleId}
      path={path}
      search={search}
      type={TrackingPageTypeWithId.HelpCenterArticle}
    >
      <TrackContentPageView
        pageTitle={translations.articlePage.seo_page_title}
      />
      <LegacyPage
        locale={locale}
        endpoints={helpCenter}
        activeSectionId={activeSectionId}
        canonicalUrl={canonical}
        trackingClient={TrackingClient}
        translations={translations}
        navigateClient={navigateClient}
        activeArticle={activeArticle}
        relatedArticles={relatedArticles}
        sections={sections}
        alternateLink={alternateLink}
        isVip={isVip}
        isShowOldHelpCenterEnabled={isShowOldHelpCenterEnabled}
        CloudflareTurnstile={CloudflareTurnstile}
        isDesktop
        hasDroppedSubdomain={hasDroppedMobileSubdomain}
      />
    </PageTrackingContextProvider>
  );
};

const PageWithRedial = redial.provideHooks(redialHandle)(
  ConnectedHelpCenterArticlePage,
);

export const ArticlePage = withLegacyProviders(PageWithRedial);
